import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "@/lib/apiConfig";
import { Facebook, Instagram, Twitter, Youtube } from "lucide-react";

const DEFAULT_LINKS = {
  social: [],
  pages: [
    { name: "Comandă melodie", url: "/comanda" },
    { name: "Pachete", url: "/pachete" },
    { name: "Recenzii", url: "/recenzii" },
    { name: "Contact", url: "/contact" },
  ],
  legal: [
    { name: "Termeni și Condiții", url: "/legal/termeni" },
    { name: "Politica de Confidențialitate", url: "/legal/confidentialitate" },
    { name: "Politica Cookie", url: "/legal/cookie" },
    { name: "Politica de Refund", url: "/legal/refund" },
  ],
};

const Footer = () => {
  const [links, setLinks] = useState({
    social: [],
    pages: [],
    legal: [],
  });

  useEffect(() => {
    const loadLinks = async () => {
      try {
        const response = await fetch(apiUrl("/public/settings/footer_links"));
        const data = await response.json();
        if (data?.value) setLinks(data.value);
      } catch {
        setLinks(DEFAULT_LINKS);
      }
    };
    loadLinks();
  }, []);

  const getIcon = (iconName) => {
    const icons = {
      facebook: Facebook,
      instagram: Instagram,
      twitter: Twitter,
      youtube: Youtube,
    };
    return icons[iconName?.toLowerCase()] || null;
  };

  const isExternal = (url) => /^https?:\/\//i.test(url || "");

  const sectionLabel = (key) => {
    const map = { pages: "Platformă", legal: "Legal" };
    if (map[key]) return map[key];
    return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
  };

  const linkSections = Object.keys(links).filter(
    (k) => k !== "social" && Array.isArray(links[k]) && links[k].length > 0
  );

  return (
    <footer className="border-t border-white/10 bg-black text-bone mt-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-2 md:grid-cols-7 gap-x-6 gap-y-10">
        <div className="col-span-2 md:col-span-2 md:pr-12">
          <div className="font-display text-2xl font-black tracking-tighter">
            TUNE<span className="text-crimson">×</span>FORUS
          </div>
          <p className="mt-4 text-white/60 text-sm max-w-md">
            Transformăm povestea ta în melodie. Cadouri muzicale 100% personalizate, livrate digital.
          </p>

          {Array.isArray(links.social) && links.social.length > 0 && (
            <div className="mt-6 flex items-center gap-3">
              {links.social.map((social, idx) => {
                const Icon = getIcon(social.icon);
                return (
                  <a
                    key={idx}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 flex items-center justify-center bg-white/5 hover:bg-crimson transition rounded"
                    aria-label={social.name}
                  >
                    {Icon ? <Icon className="w-4 h-4" /> : <span className="text-xs">{social.name?.charAt(0)}</span>}
                  </a>
                );
              })}
            </div>
          )}

          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-white/30">
            未来はここにある · the future is here
          </p>
        </div>

        {linkSections.map((key, idx) => (
          <div key={key} className={`min-w-0 ${idx === 0 ? "md:ml-10" : ""}`}>
            <h3 className="font-mono text-[11px] uppercase tracking-[0.18em] text-crimson whitespace-nowrap">
              {sectionLabel(key)}
            </h3>
            <ul className="mt-3 space-y-1.5 text-sm text-white/70">
              {links[key].map((item, idx) => (
                <li key={idx}>
                  {isExternal(item.url) ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-white transition inline-block"
                    >
                      {item.name}
                    </a>
                  ) : (
                    <Link to={item.url || "#"} className="hover:text-white transition inline-block">
                      {item.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-white/5 py-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/30 text-center md:text-left">
              © {new Date().getFullYear()} TuneForUs · Toate drepturile rezervate
            </div>

            <div className="flex items-center gap-6 flex-wrap justify-center md:justify-end">
              <a
                href="https://anpc.ro/sal/"
                target="_blank"
                rel="noopener noreferrer"
                className="transition hover:opacity-80"
              >
                <img
                  src="/legal-assets/sal.svg"
                  alt="SAL - Soluționare Alternative Litigii"
                  width="250"
                  height="50"
                  className="h-[50px] w-auto max-w-full"
                  style={{ maxWidth: "250px" }}
                />
              </a>

              <a
                href="https://consumer-redress.ec.europa.eu/"
                target="_blank"
                rel="noopener noreferrer"
                className="transition hover:opacity-80"
              >
                <img
                  src="/legal-assets/sol.svg"
                  alt="SOL - Soluționare Online Litigii / Consumer Redress UE"
                  width="250"
                  height="50"
                  className="h-[50px] w-auto max-w-full"
                  style={{ maxWidth: "250px" }}
                />
              </a>
            </div>
          </div>

          <div className="mt-3 text-center text-[10px] text-white/40">
            Informare consumatori: SAL prin ANPC și informații europene Consumer Redress
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
